import React from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Form, FormGroup, FormControl, Row, Col } from 'react-bootstrap';
import { redirectUnlessInventory } from '../../utils/moduleAccess';
import SharedDataTable from '../../_components/Shared/SharedDataTable';
import {
  SHARED_TABLE_HEADER_BG,
  SHARED_TABLE_HEADER_BORDER,
} from '../../_components/Shared/dataTableStyles';
import moment from 'moment';
import { Chart } from 'chart.js';
import 'chartjs-plugin-labels';
import config from '../../../config';
import utils from '../../utils';
import { handleFocusInScrollField } from './invKeyboardScroll';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';

function isOpenTransferStatus(status) {
    const s = String(status || '').toLowerCase();
    return s === 'pending_confirmation' || s === 'pending';
}

function formatTransferStatus(status, rejectReason) {
    if (status === 'rejected') {
        return `Rejected. ${rejectReason || 'No reason provided'}`;
    }
    if (status === 'confirmed') return 'Confirmed';
    if (status === 'pending_confirmation') return 'Pending Confirmation';
    if (status === 'pending') return 'Pending';
    if (status === 'accepted') return 'Accepted';
    if (status === 'completed') return 'Completed';
    if (status === 'cancelled') return 'Cancelled';
    return status || '';
}

function buildReportNo(materialType, createdAt, id) {
    const prefix = materialType === 'FG' ? 'FG' : 'RM';
    if (createdAt && id != null) {
        return `${prefix}-${moment(createdAt).format('YYYYMMDD')}T${id}`;
    }
    return `${prefix}-${id ?? ''}`;
}

function getRmRecordType(plain) {
    const to = String((plain && plain.toLocationCode) || '').trim();
    const marker = String((plain && plain.specialRequirement) || '').toUpperCase();
    if (to === '__SCRAP__' || marker.includes('[SCRAP]')) return 'Scrap';
    const isReturn = plain && (plain.returnIndicator === true || plain.returnIndicator === 1);
    const usageByMarker = marker.includes('AUTO_USED_FOR_PRODUCTION');
    const usageByToDesc = String((plain && plain.toLocationDescription) || '').toUpperCase().includes('USED FOR PRODUCTION');
    const usageByDesc = String((plain && plain.description) || '').toUpperCase().includes('AUTO RM USAGE FOR FG TRANSFER');
    if (usageByMarker || usageByToDesc || usageByDesc) return 'RM Usage';
    if (isReturn) return 'RM Return';
    return 'RM Transfer';
}

function getFgRecordType(plain) {
    const to = String((plain && plain.toLocationCode) || '').trim();
    const marker = String((plain && plain.specialRequirement) || '').toUpperCase();
    if (to === '__SCRAP__' || marker.includes('[SCRAP]')) return 'Scrap';
    return 'FG Transfer';
}

function parseScrapReason(plain) {
    const spec = String((plain && plain.specialRequirement) || '').trim();
    if (!spec) return '';
    return spec.replace(/^\[SCRAP\]\s*/i, '').trim();
}

function isScrapTransfer(plain) {
    const to = String((plain && plain.toLocationCode) || '').trim();
    const marker = String((plain && plain.specialRequirement) || '').toUpperCase();
    return to === '__SCRAP__' || marker.includes('[SCRAP]');
}

/** Pending scrap must not appear in inventory status / transaction reports until approved. */
function shouldShowTransferInInventoryReport(plain) {
    if (!isScrapTransfer(plain)) return true;
    return String(plain.status || '').toLowerCase() === 'confirmed';
}

function chartCategoryKey(row, period) {
    const part = String(row.partNo || '').trim() || '—';
    if (period === 'all') return part;
    const m = moment(row.transactionAt);
    if (!m.isValid()) return part;
    if (period === 'weekly') {
        return `${part} · ${m.isoWeekYear()}-W${String(m.isoWeek()).padStart(2, '0')}`;
    }
    if (period === 'monthly') {
        return `${part} · ${m.format('YYYY-MM')}`;
    }
    return part;
}

/** Match Inventory Status Overview: sticky No + Part No + Description. */
const INV_REPORT_STICKY_NO_W = 56;
const INV_REPORT_STICKY_PART_W = 132;
const INV_REPORT_STICKY_DESC_W = 220;

class InventoryStatusReport extends React.Component {
    constructor(props) {
        super(props);
        const today = moment();
        this.state = {
            loading: false,
            partNoKeyword: '',
            descriptionKeyword: '',
            materialType: 'rm',
            period: 'daily',
            periodValue: today.format('DD/MM/YYYY'),
            rows: [],
        };
        this._statusChart = null;

        this.columns = [
            {
                id: 'noSticky',
                name: 'No:',
                selector: (row) => row.no,
                minWidth: `${INV_REPORT_STICKY_NO_W}px`,
                width: `${INV_REPORT_STICKY_NO_W}px`,
                sortable: true,
            },
            {
                id: 'partNoSticky',
                name: 'Part No:',
                selector: (row) => row.partNo,
                minWidth: `${INV_REPORT_STICKY_PART_W}px`,
                width: `${INV_REPORT_STICKY_PART_W}px`,
                sortable: true,
            },
            {
                id: 'descriptionSticky',
                name: 'Description:',
                selector: (row) => row.description,
                minWidth: `${INV_REPORT_STICKY_DESC_W}px`,
                width: `${INV_REPORT_STICKY_DESC_W}px`,
                sortable: true,
                wrap: true,
            },
            {
                name: 'Record type:',
                selector: (row) => row.recordType,
                minWidth: '120px',
                sortable: true,
                wrap: true,
            },
            { name: 'Date:', selector: (row) => row.dateStr, width: '100px', sortable: true },
            { name: 'Time:', selector: (row) => row.timeStr, width: '90px', sortable: true },
            { name: 'Qty:', selector: (row) => row.qty, width: '80px', sortable: true, right: true },
            { name: 'Location Area:', selector: (row) => row.locationArea, minWidth: '120px', sortable: true, wrap: true },
            { name: 'Location Description:', selector: (row) => row.locationDescription, minWidth: '140px', sortable: true, wrap: true },
            { name: 'Location Code', selector: (row) => row.locationCode, minWidth: '110px', sortable: true },
            {
                name: 'Report No:',
                selector: (row) => row.reportNo,
                minWidth: '150px',
                sortable: true,
                cell: (row) => <span className="text-muted">{row.reportNo}</span>,
            },
            { name: 'Storage Location:', selector: (row) => row.storageLocation, minWidth: '130px', sortable: true, wrap: true },
            { name: 'Invoice / GRN No:', selector: (row) => row.invoiceGrn, minWidth: '130px', sortable: true, wrap: true },
            { name: 'Supplier:', selector: (row) => row.supplier, minWidth: '120px', sortable: true, wrap: true },
            { name: 'From Location:', selector: (row) => row.fromLocation, minWidth: '130px', sortable: true, wrap: true },
            { name: 'To Location:', selector: (row) => row.toLocation, minWidth: '130px', sortable: true, wrap: true },
            { name: 'Transaction ID:', selector: (row) => row.transactionId, minWidth: '120px', sortable: true },
            { name: 'Lot No:', selector: (row) => row.lotNo, minWidth: '100px', sortable: true },
            { name: 'Supplier MFG No:', selector: (row) => row.supplierMfgNo, minWidth: '120px', sortable: true },
            { name: 'Inspection: (Yes/No)', selector: (row) => row.inspection, width: '120px', sortable: true },
            { name: 'Confirmation Date:', selector: (row) => row.confirmationDate, width: '120px', sortable: true },
            { name: 'Confirmation Time:', selector: (row) => row.confirmationTime, width: '120px', sortable: true },
            { name: 'Confirmation By:', selector: (row) => row.confirmationBy, minWidth: '120px', sortable: true, wrap: true },
            {
                name: 'Status:',
                selector: (row) => row.statusLabel,
                minWidth: '140px',
                sortable: true,
                wrap: true,
                cell: (row) => (
                    <span
                        style={{
                            color: row.statusLabel.includes('Rejected')
                                ? '#dc3545'
                                : row.statusLabel === 'Confirmed' ||
                                    row.statusLabel === 'Completed' ||
                                    row.statusLabel === 'Accepted'
                                  ? '#28a745'
                                  : row.statusLabel === 'Pending Confirmation' || row.statusLabel === 'Pending'
                                    ? '#ffc107'
                                    : '#6c757d',
                            fontWeight: 500,
                        }}
                    >
                        {row.statusLabel}
                    </span>
                ),
            },
            { name: 'Remarks:', selector: (row) => row.remarks, minWidth: '140px', sortable: true, wrap: true },
        ];
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.rows !== this.state.rows ||
            prevState.period !== this.state.period ||
            prevState.loading !== this.state.loading
        ) {
            setTimeout(() => this.drawStatusChart(), 50);
        }
    }

    componentWillUnmount() {
        if (this._statusChart) {
            this._statusChart.destroy();
            this._statusChart = null;
        }
    }

    buildLocationAreaMap = (locationList) => {
        const list = Array.isArray(locationList) ? locationList : [];
        const map = {};
        list.forEach((loc) => {
            const code = String(loc.locationCode || '').trim();
            if (code) map[code] = loc.area || '';
        });
        return map;
    };

    getCurrentPeriodValue = (period) => {
        const now = moment();
        if (period === 'all') return '';
        if (period === 'weekly') return `${now.isoWeekYear()}-W${String(now.isoWeek()).padStart(2, '0')}`;
        if (period === 'monthly') return now.format('YYYY-MM');
        return now.format('DD/MM/YYYY');
    };

    getPeriodInputType = () => {
        if (this.state.period === 'all') return 'text';
        if (this.state.period === 'weekly') return 'week';
        if (this.state.period === 'monthly') return 'month';
        return 'date';
    };

    getPeriodRange = () => {
        const { period, periodValue } = this.state;
        if (period === 'all') return { fromM: null, toM: null };
        if (!periodValue) return { fromM: null, toM: null };

        if (period === 'weekly') {
            const weekMatch = String(periodValue).match(/^(\d{4})-W(\d{2})$/);
            if (!weekMatch) return { fromM: null, toM: null };
            const year = Number(weekMatch[1]);
            const week = Number(weekMatch[2]);
            const fromM = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek');
            const toM = moment().isoWeekYear(year).isoWeek(week).endOf('isoWeek');
            return { fromM, toM };
        }

        if (period === 'monthly') {
            const fromM = moment(periodValue, 'YYYY-MM', true).startOf('month');
            const toM = moment(periodValue, 'YYYY-MM', true).endOf('month');
            return { fromM, toM };
        }

        const parsedDaily = moment(periodValue, 'DD/MM/YYYY', true).isValid()
            ? moment(periodValue, 'DD/MM/YYYY', true)
            : moment(periodValue, 'YYYY-MM-DD', true);
        const fromM = parsedDaily.clone().startOf('day');
        const toM = parsedDaily.clone().endOf('day');
        return { fromM, toM };
    };

    handlePeriodChange = (e) => {
        const period = e.target.value;
        this.setState({
            period,
            periodValue: this.getCurrentPeriodValue(period),
        });
    };

    buildReceivingLookup = (receivingList) => {
        const byPartLot = new Map();
        const byPart = new Map();
        const arr = Array.isArray(receivingList) ? receivingList : [];
        arr.forEach((rec) => {
            const part = String(rec.partNo || '').trim();
            const lot = String(rec.lotNo || '').trim();
            if (!part) return;
            const fullKey = `${part}\t${lot}`;
            if (lot && !byPartLot.has(fullKey)) byPartLot.set(fullKey, rec);
            if (!byPart.has(part)) byPart.set(part, rec);
        });
        return { byPartLot, byPart };
    };

    pickReceiving = (lookup, partNo, lotNo) => {
        if (!lookup) return null;
        const part = String(partNo || '').trim();
        const lot = String(lotNo || '').trim();
        if (lot) {
            const hit = lookup.byPartLot.get(`${part}\t${lot}`);
            if (hit) return hit;
        }
        return lookup.byPart.get(part) || null;
    };

    mapTransferToRow = (item, materialType, lookup, areaByCode) => {
        const plain = item && typeof item === 'object' ? item : {};
        const createdAt = plain.createdAt ? moment(plain.createdAt) : null;
        const updatedAt = plain.updatedAt ? moment(plain.updatedAt) : null;
        const status = plain.status || '';
        const closed = !isOpenTransferStatus(status);
        const rec = materialType === 'RM' ? this.pickReceiving(lookup, plain.partNo, plain.lotNo) : null;

        const invoiceGrn = rec
            ? [rec.invoiceNo, rec.grnNo].filter(Boolean).join(' / ')
            : '';
        const supplier = rec ? rec.supplier || '' : '';
        const supplierMfgNo = rec ? rec.supplierMfgLotNo || '' : '';

        let inspection = '';
        if (rec && rec.inspection) inspection = String(rec.inspection).trim();
        else if (status === 'confirmed') inspection = 'Yes';
        else if (status === 'rejected') inspection = 'No';

        const confirmationDate = closed && updatedAt ? updatedAt.format('DD/MM/YYYY') : '';
        const confirmationTime = closed && updatedAt ? updatedAt.format('HH:mm:ss') : '';
        const confirmationBy = closed ? plain.updatedBy || '' : '';

        const isScrap = isScrapTransfer(plain);
        const fromCode = String(plain.fromLocationCode || '').trim();
        const toCode = String(plain.toLocationCode || '').trim();
        const locationCodeForArea = isScrap ? fromCode : toCode;
        const locationArea = (areaByCode && areaByCode[locationCodeForArea]) || '';

        const remarks = isScrap
            ? parseScrapReason(plain) || plain.specialRequirement || ''
            : materialType === 'FG'
              ? plain.remarks || ''
              : [plain.rejectReason && status === 'rejected' ? plain.rejectReason : '', plain.specialRequirement || '']
                    .filter(Boolean)
                    .join(' — ');

        return {
            _key: `${materialType}-${plain.id}`,
            recordId: plain.id,
            materialType,
            recordType: materialType === 'RM' ? getRmRecordType(plain) : getFgRecordType(plain),
            transactionAt: plain.createdAt,
            reportNo: buildReportNo(materialType, plain.createdAt, plain.id),
            dateStr: createdAt && createdAt.isValid() ? createdAt.format('DD/MM/YYYY') : '',
            timeStr: createdAt && createdAt.isValid() ? createdAt.format('HH:mm:ss') : '',
            partNo: plain.partNo || '',
            description: plain.description || '',
            qty: Number(plain.quantity || 0),
            locationArea,
            locationDescription: isScrap ? plain.fromLocationDescription || '' : plain.toLocationDescription || '',
            locationCode: isScrap ? fromCode : toCode,
            storageLocation: plain.storageLocation || '',
            invoiceGrn,
            supplier,
            fromLocation: [plain.fromLocationCode, plain.fromLocationDescription].filter(Boolean).join(' — '),
            toLocation: [plain.toLocationCode, plain.toLocationDescription].filter(Boolean).join(' — '),
            transactionId: plain.transactionId || '',
            lotNo: plain.lotNo || '',
            supplierMfgNo,
            inspection,
            confirmationDate,
            confirmationTime,
            confirmationBy,
            statusLabel: formatTransferStatus(status, plain.rejectReason),
            remarks,
            isOpen: isOpenTransferStatus(status),
        };
    };

    mapReceivingToRow = (item, areaByCode) => {
        const plain = item && typeof item === 'object' ? item : {};
        const createdAt = plain.createdAt ? moment(plain.createdAt) : null;
        const updatedAt = plain.updatedAt ? moment(plain.updatedAt) : null;
        const status = plain.status || '';
        const closed = status === 'accepted' || status === 'rejected';

        const invoiceGrn = [plain.invoiceNo, plain.grnNo].filter(Boolean).join(' / ');
        const supplier = plain.supplier || '';
        const supplierMfgNo = plain.supplierMfgLotNo || '';

        let inspection = '';
        if (plain.inspection) inspection = String(plain.inspection).trim();

        const updatedBy = plain.updated_by || plain.updatedBy || '';
        const confirmationDate = closed && updatedAt ? updatedAt.format('DD/MM/YYYY') : '';
        const confirmationTime = closed && updatedAt ? updatedAt.format('HH:mm:ss') : '';
        const confirmationBy = closed ? updatedBy : '';

        const locCode = String(plain.locationCode || '').trim();
        const locationArea = (areaByCode && areaByCode[locCode]) || '';

        const remarks = [plain.rejectReason && status === 'rejected' ? plain.rejectReason : '', plain.specialRequirement || '']
            .filter(Boolean)
            .join(' — ');

        const reportNo =
            createdAt && plain.id != null
                ? `RMR-${moment(createdAt).format('YYYYMMDD')}T${plain.id}`
                : `RMR-${plain.id ?? ''}`;

        return {
            _key: `RM-REC-${plain.id}`,
            recordId: plain.id,
            materialType: 'RM',
            recordType: 'RM Receiving',
            transactionAt: plain.createdAt,
            reportNo,
            dateStr: createdAt && createdAt.isValid() ? createdAt.format('DD/MM/YYYY') : '',
            timeStr: createdAt && createdAt.isValid() ? createdAt.format('HH:mm:ss') : '',
            partNo: plain.partNo || '',
            description: plain.description || '',
            qty: Number(plain.quantity || 0),
            locationArea,
            locationDescription: plain.locationDescription || '',
            locationCode: plain.locationCode || '',
            storageLocation: plain.storageLocation || '',
            invoiceGrn,
            supplier,
            fromLocation: '',
            toLocation: [plain.locationCode, plain.locationDescription].filter(Boolean).join(' — '),
            transactionId: plain.transactionId || '',
            lotNo: plain.lotNo || '',
            supplierMfgNo,
            inspection,
            confirmationDate,
            confirmationTime,
            confirmationBy,
            statusLabel: formatTransferStatus(status, plain.rejectReason),
            remarks,
            isOpen: String(status || '').toLowerCase() === 'pending',
        };
    };

    mapFGDeliveryToRow = (item, areaByCode) => {
        const plain = item && typeof item === 'object' ? item : {};
        const createdAt = plain.createdAt ? moment(plain.createdAt) : null;
        const updatedAt = plain.updatedAt ? moment(plain.updatedAt) : null;
        const status = plain.status || '';
        const closed = !isOpenTransferStatus(status);

        let inspection = '';
        if (status === 'confirmed') inspection = 'Yes';
        else if (status === 'rejected') inspection = 'No';

        const confirmationDate = closed && updatedAt ? updatedAt.format('DD/MM/YYYY') : '';
        const confirmationTime = closed && updatedAt ? updatedAt.format('HH:mm:ss') : '';
        const confirmationBy = closed ? plain.updatedBy || '' : '';

        const toCode = String(plain.toLocationCode || '').trim();
        const locationArea = (areaByCode && areaByCode[toCode]) || '';

        const invoiceGrn = [plain.poNo, plain.deliveryOrder].filter(Boolean).join(' / ');
        const supplier = [plain.carrier, plain.carrierOther].filter(Boolean).join(' — ') || '';

        const reportNo =
            createdAt && plain.id != null
                ? `FGD-${moment(createdAt).format('YYYYMMDD')}T${plain.id}`
                : `FGD-${plain.id ?? ''}`;

        return {
            _key: `FG-DEL-${plain.id}`,
            recordId: plain.id,
            materialType: 'FG',
            recordType: 'FG Delivery',
            transactionAt: plain.createdAt,
            reportNo,
            dateStr: createdAt && createdAt.isValid() ? createdAt.format('DD/MM/YYYY') : '',
            timeStr: createdAt && createdAt.isValid() ? createdAt.format('HH:mm:ss') : '',
            partNo: plain.partNo || '',
            description: plain.description || '',
            qty: Number(plain.quantity || 0),
            locationArea,
            locationDescription: plain.toLocationDescription || '',
            locationCode: plain.toLocationCode || '',
            storageLocation: plain.storageLocation || '',
            invoiceGrn,
            supplier,
            fromLocation: [plain.fromLocationCode, plain.fromLocationDescription].filter(Boolean).join(' — '),
            toLocation: [plain.toLocationCode, plain.toLocationDescription].filter(Boolean).join(' — '),
            transactionId: plain.transactionId || '',
            lotNo: plain.lotNo || '',
            supplierMfgNo: plain.carrierNo || '',
            inspection,
            confirmationDate,
            confirmationTime,
            confirmationBy,
            statusLabel: formatTransferStatus(status, plain.rejectReason),
            remarks: plain.remarks || '',
            isOpen: isOpenTransferStatus(status),
        };
    };

    loadReport = async () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user.accountId) return;

        this.setState({ loading: true });
        const company = user.accountId;
        const { materialType } = this.state;
        const { fromM, toM } = this.getPeriodRange();

        try {
            const locPromise = fetch(`${config.apiUrl}/getMasterLocations?company=${company}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            }).then((r) => r.json());

            let transfersPromise;
            let deliveriesPromise = Promise.resolve([]);
            let receivingPromise = Promise.resolve([]);
            if (materialType === 'rm') {
                transfersPromise = fetch(`${config.apiUrl}/getRMMaterialTransfers?company=${company}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                }).then((r) => r.json());
                receivingPromise = fetch(`${config.apiUrl}/getReceiving?company=${company}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                }).then((r) => r.json());
            } else {
                transfersPromise = fetch(`${config.apiUrl}/getFGMaterialTransfers?company=${company}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                }).then((r) => r.json());
                deliveriesPromise = fetch(`${config.apiUrl}/getFGDeliveries?company=${company}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                }).then((r) => r.json());
            }

            const [locationsRaw, transfersRaw, deliveriesRaw, receivingRaw] = await Promise.all([
                locPromise,
                transfersPromise,
                deliveriesPromise,
                receivingPromise,
            ]);
            const areaByCode = this.buildLocationAreaMap(locationsRaw);
            const transfers = (Array.isArray(transfersRaw) ? transfersRaw : []).filter((t) =>
                shouldShowTransferInInventoryReport(t)
            );
            const deliveries = Array.isArray(deliveriesRaw) ? deliveriesRaw : [];
            const receivingList = Array.isArray(receivingRaw) ? receivingRaw : [];
            const lookup = materialType === 'rm' ? this.buildReceivingLookup(receivingList) : null;

            let rows =
                materialType === 'rm'
                    ? [
                          ...transfers.map((t) => this.mapTransferToRow(t, 'RM', lookup, areaByCode)),
                          ...receivingList.map((r) => this.mapReceivingToRow(r, areaByCode)),
                      ]
                    : [
                          ...transfers.map((t) => this.mapTransferToRow(t, 'FG', null, areaByCode)),
                          ...deliveries.map((d) => this.mapFGDeliveryToRow(d, areaByCode)),
                      ];

            if (fromM && fromM.isValid()) {
                rows = rows.filter((row) => {
                    const m = moment(row.transactionAt);
                    return m.isValid() && m.isSameOrAfter(fromM);
                });
            }
            if (toM && toM.isValid()) {
                rows = rows.filter((row) => {
                    const m = moment(row.transactionAt);
                    return m.isValid() && m.isSameOrBefore(toM);
                });
            }

            const pn = (this.state.partNoKeyword || '').trim().toLowerCase();
            if (pn) {
                rows = rows.filter((row) => String(row.partNo || '').toLowerCase().includes(pn));
            }
            const desc = (this.state.descriptionKeyword || '').trim().toLowerCase();
            if (desc) {
                rows = rows.filter((row) => String(row.description || '').toLowerCase().includes(desc));
            }

            rows.sort((a, b) => moment(b.transactionAt).valueOf() - moment(a.transactionAt).valueOf());
            rows.forEach((row, idx) => {
                row.no = idx + 1;
            });

            this.setState({ rows, loading: false });
        } catch (e) {
            console.error('Inventory status report load error', e);
            this.setState({ rows: [], loading: false });
        }
    };

    drawStatusChart = () => {
        const canvas = document.getElementById('inventoryStatusReportChart');
        if (!canvas) return;

        if (this._statusChart) {
            this._statusChart.destroy();
            this._statusChart = null;
        }

        if (this.state.loading || !this.state.rows || this.state.rows.length === 0) {
            return;
        }

        const { period } = this.state;
        const buckets = new Map();
        this.state.rows.forEach((row) => {
            const label = chartCategoryKey(row, period);
            if (!buckets.has(label)) buckets.set(label, { open: 0, closed: 0 });
            const b = buckets.get(label);
            if (row.isOpen) b.open += 1;
            else b.closed += 1;
        });

        const labels = [...buckets.keys()].sort((a, c) => a.localeCompare(c));
        const openData = labels.map((l) => buckets.get(l).open);
        const closedData = labels.map((l) => buckets.get(l).closed);

        const wrap = document.getElementById('inventoryStatusReportChartWrap');
        if (wrap) {
            wrap.style.height = `${Math.max(320, Math.min(640, 280 + Math.min(labels.length, 20) * 14))}px`;
        }

        const ctx = canvas.getContext('2d');
        this._statusChart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Open (transactions)',
                        data: openData,
                        backgroundColor: 'rgba(255, 193, 7, 0.85)',
                        borderColor: 'rgba(200, 150, 0, 1)',
                        borderWidth: 1,
                    },
                    {
                        label: 'Closed (transactions)',
                        data: closedData,
                        backgroundColor: 'rgba(40, 167, 69, 0.85)',
                        borderColor: 'rgba(25, 120, 50, 1)',
                        borderWidth: 1,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: {
                        top: 16,
                        bottom: 8,
                    },
                },
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                            scaleLabel: {
                                display: true,
                                labelString:
                                    period === 'all'
                                        ? 'Part No. (all periods)'
                                        : period === 'daily'
                                          ? 'Part No. (totals in selected date range)'
                                          : period === 'weekly'
                                            ? 'Part No. · ISO week'
                                            : 'Part No. · Month',
                                fontStyle: 'bold',
                            },
                            ticks: {
                                autoSkip: labels.length > 12,
                                maxRotation: labels.length > 6 ? 50 : 0,
                                minRotation: labels.length > 6 ? 40 : 0,
                            },
                        },
                    ],
                    yAxes: [
                        {
                            stacked: true,
                            beginAtZero: true,
                            ticks: {
                                precision: 0,
                                stepSize: 1,
                            },
                            scaleLabel: {
                                display: true,
                                labelString: 'Total transactions',
                                fontStyle: 'bold',
                            },
                        },
                    ],
                },
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        boxWidth: 14,
                        padding: 16,
                    },
                },
                title: {
                    display: false,
                },
                tooltips: {
                    mode: 'index',
                    intersect: false,
                },
                plugins: {
                    labels: {
                        render: (args) => {
                            const n = Number(args.value);
                            if (!Number.isFinite(n)) return '';
                            const pct = Number(args.percentage);
                            const pctTxt = Number.isFinite(pct) ? `${Math.round(pct)}%` : '0%';
                            if (n === 0) return '';
                            return `${n} (${pctTxt})`;
                        },
                        precision: 0,
                        showActualPercentages: true,
                        showZero: false,
                        fontSize: 11,
                        fontStyle: 'bold',
                        fontColor: '#212529',
                        textMargin: 4,
                    },
                },
            },
        });
    };

    exportExcel = () => {
        utils.tableToExcel(
            'inventory-status-report-export-table',
            'report',
            'Inventory_Status_Report.xls'
        );
    };

    render() {
        const accessDenied = redirectUnlessInventory(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        return (
            <div className="container" style={{ marginTop: '20px' }} onFocusCapture={handleFocusInScrollField}>
                <h4>Inventory Status Report</h4>

                <div
                    style={{
                        marginBottom: '16px',
                        padding: '14px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                    }}
                >
                    <Form>
                        <Row className="g-2 align-items-end">
                            <Col xs={12} md={3}>
                                <FormGroup>
                                    <Form.Label className="small mb-1">Part No</Form.Label>
                                    <FormControl
                                        type="text"
                                        value={this.state.partNoKeyword}
                                        autoComplete="off"
                                        spellCheck={false}
                                        onChange={(e) => {
                                            const el = e?.currentTarget ?? e?.target;
                                            const v = el && typeof el.value === 'string' ? el.value : '';
                                            this.setState({ partNoKeyword: v });
                                        }}
                                        onInput={(e) => {
                                            const el = e?.currentTarget ?? e?.target;
                                            const v = el && typeof el.value === 'string' ? el.value : '';
                                            this.setState({ partNoKeyword: v });
                                        }}
                                        onPaste={(e) => {
                                            const el = e?.currentTarget ?? e?.target;
                                            if (!el) return;
                                            const apply = () => {
                                                const v = typeof el.value === 'string' ? el.value : '';
                                                this.setState({ partNoKeyword: v });
                                            };
                                            requestAnimationFrame(apply);
                                            setTimeout(apply, 0);
                                        }}
                                        placeholder="Part number contains…"
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} md={3}>
                                <FormGroup>
                                    <Form.Label className="small mb-1">Part Description</Form.Label>
                                    <FormControl
                                        type="text"
                                        value={this.state.descriptionKeyword}
                                        onChange={(e) => this.setState({ descriptionKeyword: e.target.value })}
                                        placeholder="Description contains…"
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} md={2}>
                                <FormGroup>
                                    <Form.Label className="small mb-1">Material Type</Form.Label>
                                    <Form.Select
                                        value={this.state.materialType}
                                        onChange={(e) => this.setState({ materialType: e.target.value })}
                                    >
                                        <option value="rm">Raw Material</option>
                                        <option value="fg">Finished Good</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col xs={12} md={2}>
                                <FormGroup>
                                    <Form.Label className="small mb-1">Period</Form.Label>
                                    <Form.Select
                                        value={this.state.period}
                                        onChange={this.handlePeriodChange}
                                    >
                                        <option value="all">All</option>
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col xs={12} md={3}>
                                <FormGroup>
                                    <Form.Label className="small mb-1">
                                        {this.state.period === 'all'
                                            ? 'Date range'
                                            : this.state.period === 'daily'
                                              ? 'Select Day'
                                              : this.state.period === 'weekly'
                                                ? 'Select Week'
                                                : 'Select Month'}
                                    </Form.Label>
                                    {this.state.period === 'all' ? (
                                        <FormControl type="text" value="All periods" disabled readOnly />
                                    ) : this.state.period === 'daily' ? (
                                        <FormControl
                                            type="text"
                                            placeholder="DD/MM/YYYY"
                                            value={this.state.periodValue}
                                            onChange={(e) => this.setState({ periodValue: e.target.value })}
                                        />
                                    ) : (
                                        <FormControl
                                            type={this.getPeriodInputType()}
                                            value={this.state.periodValue}
                                            onChange={(e) => this.setState({ periodValue: e.target.value })}
                                        />
                                    )}
                                </FormGroup>
                            </Col>
                            <Col xs={12} md="auto" className="d-flex gap-2 flex-wrap">
                                <Button variant="primary" onClick={this.loadReport} disabled={this.state.loading}>
                                    {this.state.loading ? 'Loading…' : 'Search'}
                                </Button>
                                <Button variant="outline-secondary" onClick={this.exportExcel} disabled={!this.state.rows.length}>
                                    Export
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </div>

                <style>
                    {`
                        .inventory-status-report-dt .rdt_TableHeadRow .rdt_TableCol:nth-child(1) {
                            position: sticky !important;
                            left: 0 !important;
                            z-index: 53 !important;
                            background-color: ${SHARED_TABLE_HEADER_BG} !important;
                            min-width: ${INV_REPORT_STICKY_NO_W}px !important;
                            border-right: 1px solid ${SHARED_TABLE_HEADER_BORDER} !important;
                        }
                        .inventory-status-report-dt .rdt_TableHeadRow .rdt_TableCol:nth-child(2) {
                            position: sticky !important;
                            left: ${INV_REPORT_STICKY_NO_W}px !important;
                            z-index: 52 !important;
                            background-color: ${SHARED_TABLE_HEADER_BG} !important;
                            min-width: ${INV_REPORT_STICKY_PART_W}px !important;
                            border-right: 1px solid ${SHARED_TABLE_HEADER_BORDER} !important;
                        }
                        .inventory-status-report-dt .rdt_TableHeadRow .rdt_TableCol:nth-child(3) {
                            position: sticky !important;
                            left: ${INV_REPORT_STICKY_NO_W + INV_REPORT_STICKY_PART_W}px !important;
                            z-index: 51 !important;
                            background-color: ${SHARED_TABLE_HEADER_BG} !important;
                            min-width: ${INV_REPORT_STICKY_DESC_W}px !important;
                            border-right: 1px solid ${SHARED_TABLE_HEADER_BORDER} !important;
                            box-shadow: 10px 0 16px -8px rgba(0, 0, 0, 0.28) !important;
                        }
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableCell:nth-child(1) {
                            position: sticky !important;
                            left: 0 !important;
                            z-index: 34 !important;
                            min-width: ${INV_REPORT_STICKY_NO_W}px !important;
                            width: ${INV_REPORT_STICKY_NO_W}px !important;
                            box-sizing: border-box !important;
                            border-right: 1px solid ${SHARED_TABLE_HEADER_BORDER} !important;
                        }
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableCell:nth-child(2) {
                            position: sticky !important;
                            left: ${INV_REPORT_STICKY_NO_W}px !important;
                            z-index: 33 !important;
                            min-width: ${INV_REPORT_STICKY_PART_W}px !important;
                            width: ${INV_REPORT_STICKY_PART_W}px !important;
                            max-width: 320px !important;
                            box-sizing: border-box !important;
                            border-right: 1px solid ${SHARED_TABLE_HEADER_BORDER} !important;
                        }
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableCell:nth-child(3) {
                            position: sticky !important;
                            left: ${INV_REPORT_STICKY_NO_W + INV_REPORT_STICKY_PART_W}px !important;
                            z-index: 32 !important;
                            min-width: ${INV_REPORT_STICKY_DESC_W}px !important;
                            width: ${INV_REPORT_STICKY_DESC_W}px !important;
                            max-width: 420px !important;
                            box-sizing: border-box !important;
                            border-right: 1px solid ${SHARED_TABLE_HEADER_BORDER} !important;
                            box-shadow: 10px 0 16px -8px rgba(0, 0, 0, 0.28) !important;
                        }
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableRow:nth-child(odd) .rdt_TableCell:nth-child(1),
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableRow:nth-child(odd) .rdt_TableCell:nth-child(2),
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableRow:nth-child(odd) .rdt_TableCell:nth-child(3) {
                            background-color: #f8f9fa !important;
                        }
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableRow:nth-child(even) .rdt_TableCell:nth-child(1),
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableRow:nth-child(even) .rdt_TableCell:nth-child(2),
                        .inventory-status-report-dt .rdt_TableBody .rdt_TableRow:nth-child(even) .rdt_TableCell:nth-child(3) {
                            background-color: #ffffff !important;
                        }
                    `}
                </style>
                <SharedDataTable
                    className="inventory-status-report-dt"
                    columns={this.columns}
                    data={this.state.rows}
                    keyField="_key"
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    responsive={false}
                    progressPending={this.state.loading}
                    progressComponent={<DatacoLoadingIndicator loading={this.state.loading} size="compact" label="Loading inventory status report" />}
                    noDataComponent="No rows — set filters and click Search."
                    customStyles={{
                        responsiveWrapper: {
                            style: {
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                position: 'relative',
                            },
                        },
                        cells: {
                            style: {
                                position: 'relative',
                                zIndex: 0,
                            },
                        },
                    }}
                />

                <div className="mt-4 mb-2">
                    <h5 className="mb-2">Open vs closed transactions</h5>
                    <div
                        id="inventoryStatusReportChartWrap"
                        style={{ minHeight: '320px', height: '400px', position: 'relative' }}
                    >
                        <canvas id="inventoryStatusReportChart" />
                    </div>
                </div>

                <div id="inventory-status-report-export-table" style={{ display: 'none' }}>
                    <table border="1" cellPadding="4" style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th>No:</th>
                                <th>Part No:</th>
                                <th>Description:</th>
                                <th>Record type:</th>
                                <th>Date:</th>
                                <th>Time:</th>
                                <th>Qty:</th>
                                <th>Location Area:</th>
                                <th>Location Description:</th>
                                <th>Location Code</th>
                                <th>Report No:</th>
                                <th>Storage Location:</th>
                                <th>Invoice / GRN No:</th>
                                <th>Supplier:</th>
                                <th>From Location:</th>
                                <th>To Location:</th>
                                <th>Transaction ID:</th>
                                <th>Lot No:</th>
                                <th>Supplier MFG No:</th>
                                <th>Inspection: (Yes/No)</th>
                                <th>Confirmation Date:</th>
                                <th>Confirmation Time:</th>
                                <th>Confirmation By:</th>
                                <th>Status:</th>
                                <th>Remarks:</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(this.state.rows || []).map((r) => (
                                <tr key={r._key}>
                                    <td>{r.no}</td>
                                    <td>{r.partNo}</td>
                                    <td>{r.description}</td>
                                    <td>{r.recordType}</td>
                                    <td>{r.dateStr}</td>
                                    <td>{r.timeStr}</td>
                                    <td>{r.qty}</td>
                                    <td>{r.locationArea}</td>
                                    <td>{r.locationDescription}</td>
                                    <td>{r.locationCode}</td>
                                    <td>{r.reportNo}</td>
                                    <td>{r.storageLocation}</td>
                                    <td>{r.invoiceGrn}</td>
                                    <td>{r.supplier}</td>
                                    <td>{r.fromLocation}</td>
                                    <td>{r.toLocation}</td>
                                    <td>{r.transactionId}</td>
                                    <td>{r.lotNo}</td>
                                    <td>{r.supplierMfgNo}</td>
                                    <td>{r.inspection}</td>
                                    <td>{r.confirmationDate}</td>
                                    <td>{r.confirmationTime}</td>
                                    <td>{r.confirmationBy}</td>
                                    <td>{r.statusLabel}</td>
                                    <td>{r.remarks}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export { InventoryStatusReport };
